import { useTranslation } from '../i18n';
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@parsina/framework';

/**
 * Keyboard shortcuts — the clinic app's central registry.
 *
 * The clinic app is keyboard-focused by doctrine (clinic-app/CLAUDE.md §4.2): the
 * پذیرش desk and the حسابداری desk are wide monitors running the same few flows hundreds of
 * times a day, and the benchmark is invoice entry in under 30 seconds. That is only
 * reachable if the high-frequency actions have keys.
 *
 * Everything registers HERE. Screens never attach their own `window.addEventListener`
 * for a shortcut, because two screens binding the same key independently is invisible
 * until they are mounted together, and because a key nobody can enumerate is a key
 * nobody discovers — the `?` overlay can only list what the registry knows.
 *
 *   useShortcut('f3', () => inputRef.current?.focus(), { label: 'جست‌وجوی بیمار' })
 *
 * Precedence is last-registered-wins, so a modal that registers `esc` shadows the
 * screen underneath it for as long as it is mounted, then hands the key back.
 */

/**
 * Two contexts, deliberately.
 *
 * `RegistryCtx` must keep a STABLE identity for the life of the provider, because
 * `useShortcut` lists it as an effect dependency. A single context carrying both the
 * registry and the overlay state would change identity on every registration, which
 * would re-run every consumer's effect, which registers, which bumps again — an
 * infinite loop that only shows up once something actually binds a key.
 */
const RegistryCtx = createContext(null);
const HelpCtx = createContext(null);

/** Monotonic registration counter — higher wins when several handlers share a combo. */
let SEQ = 0;

const FKEY = /^f([1-9]|1[0-2])$/;

/**
 * Canonical form of a combo string: modifiers in a fixed order, lowercase, `esc` for
 * Escape. `'Ctrl+S'`, `'ctrl+s'` and `'CTRL + S'` all normalize to `'ctrl+s'`.
 */
export const normalizeCombo = (raw) => {
  const parts = String(raw || '').toLowerCase().split('+').map((s) => s.trim()).filter(Boolean);
  let key = '';
  const mods = new Set();
  for (const p of parts) {
    if (p === 'ctrl' || p === 'cmd' || p === 'meta') mods.add('ctrl');
    else if (p === 'alt' || p === 'option') mods.add('alt');
    else if (p === 'shift') mods.add('shift');
    else key = p === 'escape' ? 'esc' : p;
  }
  if (!key) return '';
  const out = [];
  if (mods.has('ctrl')) out.push('ctrl');
  if (mods.has('alt')) out.push('alt');
  if (mods.has('shift')) out.push('shift');
  out.push(key);
  return out.join('+');
};

/**
 * A digit is a digit on any layout. The Persian keyboard's 1 key types «۱» (and an
 * Arabic one «١»), so without this a key bound to '1' — «منشی هوشمند»'s «کدام‌یک؟» —
 * would answer only a receptionist who had switched to English first.
 */
const foldDigit = (key) => {
  if (key.length !== 1) return key;
  const code = key.charCodeAt(0);
  if (code >= 0x06F0 && code <= 0x06F9) return String(code - 0x06F0);
  if (code >= 0x0660 && code <= 0x0669) return String(code - 0x0660);
  return key;
};

/**
 * The combo a KeyboardEvent represents.
 *
 * Shift is only part of the combo for keys that are not already a shifted character:
 * `?` is Shift+/ on most layouts and on the Persian layout, so requiring an explicit
 * `shift+` there would make the help key unmatchable. Meta is folded into ctrl so a
 * Mac gets ⌘S for free.
 */
export const comboFromEvent = (e) => {
  let key = e?.key;
  if (!key) return '';
  key = key === 'Escape' ? 'esc' : key === ' ' ? 'space' : foldDigit(key.toLowerCase());
  const out = [];
  if (e.ctrlKey || e.metaKey) out.push('ctrl');
  if (e.altKey) out.push('alt');
  if (e.shiftKey && key.length > 1) out.push('shift');
  out.push(key);
  return out.join('+');
};

/** True while the caret is somewhere typing happens — `F3` must still reach us, `?` must not. */
export const isEditableTarget = (el) => {
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable === true;
};

/**
 * Whether a combo fires while the caret is in a field, when the caller does not say.
 *
 * Function keys, Escape and anything with a modifier are safe: no layout produces them
 * as text. A bare printable key is not — `?` inside the search box is a question mark
 * the receptionist is typing, not a request for help.
 */
export const firesInInputByDefault = (combo) => {
  const key = combo.split('+').pop();
  return combo.includes('ctrl+') || combo.includes('alt+') || key === 'esc' || FKEY.test(key);
};

/** Human-readable key caps for the help overlay, right-to-left friendly. */
const CAP = { ctrl: 'Ctrl', alt: 'Alt', shift: 'Shift', esc: 'Esc', space: 'Space' };
export const comboLabel = (combo) =>
  combo.split('+').map((p) => CAP[p] || (FKEY.test(p) ? p.toUpperCase() : p.toUpperCase())).join(' + ');

export const ShortcutProvider = ({ children }) => {
  // A ref, not state: registering must not re-render the tree that is registering.
  const regs = useRef(new Map());
  const [revision, bump] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  const register = useCallback((id, def) => {
    regs.current.set(id, def);
    bump((v) => v + 1);
    return () => {
      regs.current.delete(id);
      bump((v) => v + 1);
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      const combo = comboFromEvent(e);
      if (!combo) return;
      const editable = isEditableTarget(e.target);

      let best = null;
      for (const def of regs.current.values()) {
        if (def.combo !== combo) continue;
        const fires = def.firesInInput ?? firesInInputByDefault(def.combo);
        if (editable && !fires) continue;
        if (!best || def.seq > best.seq) best = def;   // last registered wins
      }
      if (!best) return;

      // Claim the key only once we know something handles it, so an unbound F5 still
      // reloads and Ctrl+S in a field the app does not own still belongs to the browser.
      e.preventDefault();
      e.stopPropagation();
      best.run(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Stable for the provider's lifetime: `register` is a useCallback with no deps and
  // `regs` is a ref. Nothing here may depend on `revision`.
  const registry = useMemo(() => ({ register, regs }), [register]);
  const help = useMemo(() => ({ revision, helpOpen, setHelpOpen }), [revision, helpOpen]);

  return (
    <RegistryCtx.Provider value={registry}>
      <HelpCtx.Provider value={help}>
        {children}
        <ShortcutHelp />
      </HelpCtx.Provider>
    </RegistryCtx.Provider>
  );
};

/**
 * Bind `combo` to `handler` for as long as the calling component is mounted.
 *
 * @param combo   e.g. 'f3', 'ctrl+s', 'esc', '?'
 * @param handler runs with the KeyboardEvent; already preventDefault-ed
 * @param opts.label        Persian label for the `?` overlay. Omit to stay unlisted.
 * @param opts.group        overlay section, defaults to «عمومی»
 * @param opts.enabled      false unbinds without unmounting (a disabled button's key)
 * @param opts.firesInInput override the caret rule (see firesInInputByDefault)
 */
export const useShortcut = (combo, handler, opts = {}) => {
  const { label, group: groupOpt, enabled = true, firesInInput } = opts;
  const { t } = useTranslation();
  // Resolved here rather than as a default parameter: a default is evaluated
  // before the hook body, where `t` does not exist yet. Both `label` and
  // `group` arrive ALREADY TRANSLATED from the call site — they are copy the
  // caller owns — and `t`'s identity changes on a language switch, so the
  // effect below re-registers and the `?` overlay is never left in the
  // previous language.
  const group = groupOpt || t('shortcuts.groups.general');
  const registry = useContext(RegistryCtx);

  // The handler is read through a ref so a fresh closure every render does not
  // re-register — re-registering on every render would bump the provider, re-render
  // the child, and register again, forever.
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = Symbol('shortcut');

  useEffect(() => {
    if (!registry || !enabled) return undefined;
    const normalized = normalizeCombo(combo);
    if (!normalized) return undefined;
    return registry.register(idRef.current, {
      combo: normalized,
      label,
      group,
      firesInInput,
      seq: ++SEQ,
      run: (e) => handlerRef.current?.(e),
    });
  }, [registry, combo, label, group, enabled, firesInInput]);
};

/** Everything currently bound and labelled, grouped for the overlay. */
export const useShortcutList = () => {
  const registry = useContext(RegistryCtx);
  const help = useContext(HelpCtx);
  const revision = help?.revision;
  return useMemo(() => {
    if (!registry) return [];
    const groups = new Map();
    for (const def of registry.regs.current.values()) {
      if (!def.label) continue;
      if (!groups.has(def.group)) groups.set(def.group, new Map());
      // Same combo registered twice (screen + modal): the overlay shows the one that
      // would actually fire, not both.
      const byCombo = groups.get(def.group);
      const prev = byCombo.get(def.combo);
      if (!prev || def.seq > prev.seq) byCombo.set(def.combo, def);
    }
    return [...groups.entries()].map(([group, byCombo]) => ({
      group,
      items: [...byCombo.values()].sort((a, b) => a.combo.localeCompare(b.combo)),
    }));
    // `revision` is the signal that `regs` (a ref) changed — it is a dependency even
    // though the body never reads it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registry, revision]);
};

/**
 * A key cap, for putting a shortcut hint on the button it triggers.
 *
 * `aria-hidden` is not an oversight: the cap is a visual affordance, and letting it
 * into the accessible name would rename the button from «ثبت نوبت» to «ثبت نوبت F4»
 * for every screen-reader user and every query that looks the button up by name.
 * Announce the key with `aria-keyshortcuts` on the button instead — that is the
 * attribute assistive tech actually reads for this.
 */
/**
 * The `?` overlay, for a control that wants to open it by pointer. The overlay
 * is the only place a shortcut is listed, and a key nobody has been told about
 * is not a discovery surface — so the sidebar footer carries a visible way in.
 */
export const useShortcutHelp = () => {
  const help = useContext(HelpCtx);
  return {
    open: Boolean(help?.helpOpen),
    toggle: useCallback(() => help?.setHelpOpen((v) => !v), [help]),
  };
};

export const Kbd = ({ combo, style }) => (
  <kbd
    aria-hidden="true"
    style={{
      fontFamily: 'inherit', fontSize: 10, fontWeight: 700, lineHeight: 1,
      padding: '2px 5px', borderRadius: 4, whiteSpace: 'nowrap',
      background: 'rgba(255,255,255,0.22)', color: 'inherit',
      border: '1px solid rgba(255,255,255,0.28)',
      ...style,
    }}
  >
    {comboLabel(normalizeCombo(combo))}
  </kbd>
);

/**
 * The `?` overlay. Mounted once by the provider — it is the only discovery surface a
 * shortcut has, so a shortcut worth binding is a shortcut worth labelling.
 */
const ShortcutHelp = () => {
  const { t } = useTranslation();
  const help = useContext(HelpCtx);
  const groups = useShortcutList();

  useShortcut('?', () => help?.setHelpOpen((v) => !v), {
    label: t('shortcuts.overlay.title'),
    group: t('shortcuts.groups.general'),
  });

  if (!help) return null;

  return (
    <Dialog open={help.helpOpen} onOpenChange={help.setHelpOpen}>
      <DialogContent className="max-w-lg" dir="rtl">
        <DialogHeader><DialogTitle>{t('shortcuts.overlay.heading')}</DialogTitle></DialogHeader>
        {groups.length === 0 ? (
          <p style={{ fontSize: 13, opacity: 0.7 }}>{t('shortcuts.overlay.empty')}</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 4 }}>
            {groups.map(({ group, items }) => (
              <section key={group}>
                <h3 style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 800, opacity: 0.55 }}>{group}</h3>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {items.map((it) => (
                    <li
                      key={it.combo}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 12, padding: '5px 0', fontSize: 13,
                      }}
                    >
                      <span>{it.label}</span>
                      <kbd dir="ltr"
                        style={{
                          fontFamily: 'inherit', fontSize: 11, fontWeight: 700,
                          padding: '3px 7px', borderRadius: 5, unicodeBidi: 'isolate',
                          background: 'rgba(0,0,0,0.06)', border: '1px solid rgba(0,0,0,0.1)',
                        }}
                      >
                        {comboLabel(it.combo)}
                      </kbd>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default useShortcut;
